import { useState } from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/AuthStore';
import { ensureDevSession } from '@/lib/devAuth';

const API_URL = import.meta.env.VITE_API_URL ?? '';
const DEV_USER_STORAGE_KEY = 'devUser';

// Gate on the build-time dev-auto-login flag directly. `isDevAutoLogin()` also
// requires `!window.ghComponent`, which the EngageCraft shell sets
// asynchronously after the initial dev-login - so by render time it can be
// truthy and would hide the overlay. The flag is only 'true' on local/dev
// builds (never production), which is the correct visibility condition here.
const DEV_QA_ENABLED = import.meta.env.VITE_DEV_AUTO_LOGIN === 'true';

/**
 * Dev-only QA overlay. Renders nothing unless the dev-auto-login build flag is
 * set (local/dev only, never production). Gives QA one-click actions to reset
 * the current user's team for the active tournament and to hop between named
 * test users - the groundwork for validating tickets without rebuilding state
 * by hand each time.
 */
export function DevQaOverlay() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [switchName, setSwitchName] = useState('');
  const token = useAuthStore(s => s.token);

  if (!DEV_QA_ENABLED) return null;

  const currentUser =
    (typeof window !== 'undefined' && window.localStorage.getItem(DEV_USER_STORAGE_KEY)) ||
    'default';

  const resetTeam = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/dev-reset-team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `Reset failed (${res.status})`);
      }
      const body = (await res.json()) as { data?: { teamsDeleted?: number } };
      toast.success(`Team reset - ${body.data?.teamsDeleted ?? 0} team(s) cleared. Reloading...`);
      setTimeout(() => window.location.assign('/'), 900);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  const switchUser = async (name: string) => {
    const target = name.trim() || 'default';
    if (target === 'default') {
      window.localStorage.removeItem(DEV_USER_STORAGE_KEY);
    } else {
      window.localStorage.setItem(DEV_USER_STORAGE_KEY, target);
    }
    // Clear the session so ensureDevSession re-logs in as the new user.
    useAuthStore.getState().setGhAuth({ token: null, status: 'anonymous' });
    await ensureDevSession(true);
    window.location.assign('/');
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 2147483000,
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {open ? (
        <div
          style={{
            width: 260,
            background: '#10131a',
            color: '#fff',
            borderRadius: 12,
            boxShadow: '0 8px 30px rgba(0,0,0,0.45)',
            border: '1px solid #2b3242',
            padding: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <strong style={{ fontSize: 13, letterSpacing: 0.3 }}>QA TOOLS (dev)</strong>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#8b93a7',
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: 1,
              }}
              aria-label="Close QA tools"
            >
              ×
            </button>
          </div>

          <div style={{ fontSize: 12, color: '#8b93a7', marginBottom: 6 }}>
            User: <strong style={{ color: '#fff' }}>{currentUser}</strong>
          </div>

          <button
            onClick={resetTeam}
            disabled={busy}
            style={{
              width: '100%',
              padding: '9px 12px',
              background: busy ? '#3a2530' : '#e5484d',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: busy ? 'default' : 'pointer',
              fontWeight: 600,
              fontSize: 13,
              marginBottom: 10,
            }}
          >
            {busy ? 'Resetting…' : 'Reset my team (start over)'}
          </button>

          <div style={{ fontSize: 12, color: '#8b93a7', marginBottom: 6 }}>Switch test user</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={switchName}
              onChange={e => setSwitchName(e.target.value)}
              placeholder="name (or blank)"
              onKeyDown={e => e.key === 'Enter' && switchUser(switchName)}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '8px 10px',
                background: '#1b2130',
                color: '#fff',
                border: '1px solid #2b3242',
                borderRadius: 8,
                fontSize: 13,
              }}
            />
            <button
              onClick={() => switchUser(switchName)}
              style={{
                padding: '8px 12px',
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              Go
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
            Blank name = default dev user. New names are created on the fly.
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          style={{
            padding: '10px 14px',
            background: '#10131a',
            color: '#fff',
            border: '1px solid #2b3242',
            borderRadius: 999,
            boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 0.3,
          }}
        >
          🧪 QA
        </button>
      )}
    </div>
  );
}
